import { useEffect, useEffectEvent, useRef, useState } from 'react';

import Search from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';

import { useFilter } from '@/pages/Catalog/contexts/filter/useFilter';
import type { CatalogItem } from '@/types/catalog';

import PostHogTextField from './PostHogSearch';

import type { Table } from '@tanstack/react-table';

const SEARCH_DEBOUNCE_DELAY = 700;

const TableSearch = ({ table, tabId }: { table: Table<CatalogItem>; tabId: string }) => {
  const { filters, setFilter } = useFilter();

  const inputRef = useRef<HTMLInputElement>(undefined);

  const [value, setValue] = useState(filters.search ?? '');
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (value) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setFilter('search', value);
        table.setGlobalFilter(value);
      }, SEARCH_DEBOUNCE_DELAY);
    } else {
      table.setGlobalFilter('');
      if (filters.search !== null) {
        setFilter('search', null);
      }
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [filters.search, setFilter, table, value]);

  const resetSearch = useEffectEvent(() => {
    setValue('');
  });

  useEffect(() => {
    if (!filters.search) resetSearch();
  }, [filters.search]);

  return (
    <PostHogTextField
      hiddenLabel
      id={tabId}
      inputRef={inputRef}
      placeholder='Search'
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment
              position='start'
              sx={{ m: 0 }}
              onClick={() => {
                if (inputRef.current) inputRef.current.focus();
              }}
            >
              <Search color='info' />
            </InputAdornment>
          )
        }
      }}
      sx={{
        minWidth: 200,
        '& .MuiFilledInput-input': {
          p: 1.5
        },
        '& .MuiInputBase-root': {
          backgroundColor: 'secondary.main',
          height: 48,
          maxHeight: 48
        }
      }}
      value={value}
      variant='filled'
      onChange={e => {
        setValue(e.target.value);
      }}
    />
  );
};

export default TableSearch;
